import type { DynamicClient } from '@dynamic-labs-sdk/client';
import { getCore, getDefaultClient } from '@dynamic-labs-sdk/client/core';

import { pendingRequestStorageKeySchema } from '../storage/pendingRequestStorageSchema';

export type DetectPhantomRedirectParams = {
  url: URL;
};

/**
 * Detects if the current URL is a Phantom wallet redirect.
 *
 * This function examines the URL parameters to determine if it contains
 * redirect data from a Phantom wallet deep link operation, and validates
 * that there is a corresponding pending request in storage.
 *
 * Most Phantom callbacks include recognizable query parameters:
 * - **Connect**: `phantom_encryption_public_key`
 * - **Sign** (signMessage, signTransaction, etc.): `nonce` + `data`
 * - **Error** (any method): `errorCode`
 *
 * **Disconnect is the exception**: on success, Phantom redirects back to the
 * bare `redirect_link` with NO query parameters at all. To detect this case,
 * we fall back to checking whether a pending disconnect request exists in
 * storage.
 *
 * @param params.url - The URL to check for Phantom redirect parameters
 * @param client - The Dynamic client instance
 * @returns A promise that resolves to true if the URL is a valid Phantom redirect, false otherwise
 */
export const detectPhantomRedirect = async (
  { url }: DetectPhantomRedirectParams,
  client: DynamicClient = getDefaultClient()
): Promise<boolean> => {
  const core = getCore(client);

  // Phantom's connect callback includes phantom_encryption_public_key.
  // Sign callbacks (signMessage, signTransaction, etc.) only include nonce + data
  // — no phantom_encryption_public_key — because the session is already established.
  // Error callbacks from any method include errorCode.
  const hasPhantomParams =
    url.searchParams.has('phantom_encryption_public_key') ||
    url.searchParams.has('errorCode') ||
    (url.searchParams.has('nonce') && url.searchParams.has('data'));

  await core.initTrack.waitForAll();

  const pendingRequest = await core.storage.getItem(
    pendingRequestStorageKeySchema
  );

  if (!pendingRequest) {
    return false;
  }

  if (hasPhantomParams) {
    return true;
  }

  // Phantom's disconnect callback is unique: on success it redirects back to
  // the app with NO query parameters at all — no data, nonce,
  // phantom_encryption_public_key, or errorCode. The URL is just the bare
  // redirect_link. A pending disconnect request in storage confirms this
  // empty redirect is the disconnect callback.
  return pendingRequest.method === 'disconnect';
};
